import React, { useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import moment, { Moment } from 'moment';
import { Typography, Button, Space, DatePicker } from 'antd';
// Api
import { GET_AFFILIATE_COMMISSION_PAYOUTS } from 'api/streamAffiliate/queries';
// Types
import { RangePickerProps } from 'antd/es/date-picker';
import { SortDirection } from 'api/graphql-global-types';
import {
  GetAffiliateCommissionPayouts,
  GetAffiliateCommissionPayoutsVariables,
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities,
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities_streams,
} from 'api/streamAffiliate/types/GetAffiliateCommissionPayouts';
// Helpers
import { formatCurrencyString, getPublicStreamDate } from 'helpers/utils';
import { downloadCsv } from 'helpers/csv';
// UI
import Table, {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_NUMBER_ITEMS_PER_PAGE,
} from 'ui/Table';
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './AffiliateCommissionsReportTable.module.scss';

type PayoutRow =
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities;
type PayoutStreamRow =
  GetAffiliateCommissionPayouts_getAffiliateCommissionPayouts_entities_streams;

type CommissionReportItem = {
  userId: string;
  participantName: string;
  email: string;
  salesCount: number;
  commissionTotal: number;
  streams: PayoutStreamRow[];
};

const { RangePicker } = DatePicker;
const { Text } = Typography;

/** Page size for the export, which walks the whole period regardless of the table's paging. */
const EXPORT_PAGE_SIZE = 100;

const defaultStartDate = moment()
  .subtract(1, 'months')
  .startOf('day')
  .utc(true);

const defaultEndDate = moment().endOf('day').utc(true);

const getParticipantName = (user: PayoutRow['user']): string =>
  user.storeDetails?.storeName ||
  `${user.firstName || ''} ${user.lastName || ''}`.trim();

const getOrganizerName = (store: PayoutStreamRow['stream']['store']): string =>
  `${store.firstName || ''} ${store.lastName || ''}`.trim() || store.slug || '';

const toReportItems = (entities: PayoutRow[]): CommissionReportItem[] =>
  entities.map((row) => ({
    userId: row.user.id,
    participantName: getParticipantName(row.user),
    email: row.user.email ?? '',
    salesCount: row.salesCount,
    commissionTotal: row.commissionTotal,
    streams: row.streams,
  }));

// One CSV line per participant and stream, so the organizer-side deduction is explicit.
const toCsvRows = (items: CommissionReportItem[]) =>
  items.flatMap((row) =>
    row.streams.map((streamRow) => ({
      participant: row.participantName,
      email: row.email,
      organizer: getOrganizerName(streamRow.stream.store),
      stream: streamRow.stream.name || '',
      streamDate: getPublicStreamDate(streamRow.stream.scheduleDate),
      sales: streamRow.salesCount,
      commission: streamRow.commission,
    }))
  );

/**
 * What a manual payout run needs: how much each participant is owed in the period,
 * and out of which organizer's streams, so the same amount is deducted on their side.
 */
const AffiliateCommissionsReportTable = (): JSX.Element => {
  const client = useApolloClient();
  const [startDate, setStartDate] = useState<Moment>(defaultStartDate);
  const [endDate, setEndDate] = useState<Moment>(defaultEndDate);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    DEFAULT_NUMBER_ITEMS_PER_PAGE
  );

  const paidAtDateRange = {
    from: startDate.toISOString(),
    to: endDate.toISOString(),
  };

  const { data, loading } = useQuery<
    GetAffiliateCommissionPayouts,
    GetAffiliateCommissionPayoutsVariables
  >(GET_AFFILIATE_COMMISSION_PAYOUTS, {
    variables: {
      input: {
        paidAtDateRange,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        direction: SortDirection.DESC,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
    const [start, end] = dates || [];

    if (start && end) {
      setStartDate(moment.parseZone(start).utc(true).startOf('day'));
      setEndDate(moment.parseZone(end).utc(true).endOf('day'));
      setCurrentPage(DEFAULT_CURRENT_PAGE);
    }
  };

  // The table shows one page; the payout sheet must hold every participant of the period.
  const fetchAllForPeriod = async (): Promise<PayoutRow[]> => {
    const entities: PayoutRow[] = [];
    let total = 0;

    do {
      const { data: page } = await client.query<
        GetAffiliateCommissionPayouts,
        GetAffiliateCommissionPayoutsVariables
      >({
        query: GET_AFFILIATE_COMMISSION_PAYOUTS,
        variables: {
          input: {
            paidAtDateRange,
            offset: entities.length,
            limit: EXPORT_PAGE_SIZE,
            direction: SortDirection.DESC,
          },
        },
        fetchPolicy: 'network-only',
      });

      const pageEntities = page?.getAffiliateCommissionPayouts.entities ?? [];
      total = page?.getAffiliateCommissionPayouts.total ?? 0;
      entities.push(...pageEntities);

      if (pageEntities.length === 0) {
        break;
      }
    } while (entities.length < total);

    return entities;
  };

  const filename = `Affiliate_commissions_from_${moment(startDate).format(
    'YYYY-MMM-DD'
  )}_to_${moment(endDate).format('YYYY-MMM-DD')}.csv`;

  const handleExport = async () => {
    setExporting(true);

    try {
      const csvRows = toCsvRows(toReportItems(await fetchAllForPeriod()));
      downloadCsv(filename, csvRows);
      successNotification('File downloading');
    } catch (error) {
      errorNotification('Could not export the report');
    } finally {
      setExporting(false);
    }
  };

  const rows = toReportItems(
    data?.getAffiliateCommissionPayouts?.entities || []
  );

  const columns = [
    {
      title: 'Participant',
      dataIndex: 'participantName',
      key: 'participantName',
      fixed: 'left' as const,
      width: 180,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Sales',
      dataIndex: 'salesCount',
      key: 'salesCount',
      align: 'center' as const,
      width: 80,
    },
    {
      title: 'Commission owed',
      dataIndex: 'commissionTotal',
      key: 'commissionTotal',
      align: 'right' as const,
      width: 140,
      render: (commissionTotal: number) =>
        formatCurrencyString(commissionTotal),
    },
    {
      title: 'Out of',
      dataIndex: 'streams',
      key: 'streams',
      width: 320,
      render: function Streams(streams: PayoutStreamRow[]) {
        return (
          <ul className={styles.streamList}>
            {streams.map((streamRow) => (
              <li key={streamRow.stream.id}>
                {getOrganizerName(streamRow.stream.store)} ·{' '}
                {streamRow.stream.name} ·{' '}
                {formatCurrencyString(streamRow.commission)}
              </li>
            ))}
          </ul>
        );
      },
    },
  ];

  const getMaxDate = (current: Moment) =>
    moment().diff(current, 'days', true) <= 0;

  return (
    <>
      <Space
        direction="horizontal"
        align="baseline"
        className={styles.spaceWrapper}
      >
        <div>
          <Text>Paid between:</Text>
          <RangePicker
            value={[startDate, endDate]}
            allowClear={false}
            disabledDate={getMaxDate}
            onChange={handleDateRangeChange}
          />
        </div>
        <Button type="primary" loading={exporting} onClick={handleExport}>
          Export to CSV
        </Button>
      </Space>

      <Table<CommissionReportItem, string>
        columns={columns}
        data={rows}
        rowKey={(item) => item.userId}
        scroll={{ x: 1024 }}
        loading={loading}
        total={data?.getAffiliateCommissionPayouts?.total}
        currentPage={currentPage}
        pageSize={pageSize}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default AffiliateCommissionsReportTable;
